import React, { useCallback, useEffect, useRef, useState } from "react";
import { GET, POST, DELETE, alert } from "../CustomComponent";
import DrawPad from "./DrawPad";
import { prepareImage } from "./imageUtils";

const checker = {
  backgroundColor: "#fff",
  backgroundImage:
    "linear-gradient(45deg,#eef2f7 25%,transparent 25%,transparent 75%,#eef2f7 75%),linear-gradient(45deg,#eef2f7 25%,transparent 25%,transparent 75%,#eef2f7 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0,8px 8px",
};

const LABEL = {
  signature: { title: "ลายเซ็น", item: "ลายเซ็น" },
  stamp: { title: "ตราปั๊ม", item: "ตราปั๊ม" },
};

const MAX_BYTES = 5 * 1024 * 1024;

// เนื้อหาคลังลายเซ็น/ตราปั๊มของบริษัท (เก็บที่ backend) — สร้างใหม่, ลบ, และเลือกใช้ (ถ้ามี onPick)
// ใช้ซ้ำทั้งใน modal ของหน้าแก้ PDF และหน้าตั้งค่า
export default function AssetLibrary({ type, onPick }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState("library"); // library | draw | upload
  const [draft, setDraft] = useState(null); // dataUrl ที่รอตั้งชื่อ
  const [name, setName] = useState("");
  const [removeWhite, setRemoveWhite] = useState(type === "signature");
  const [isSaving, setIsSaving] = useState(false);
  const fileRef = useRef(null);
  const label = LABEL[type] || LABEL.signature;

  const refresh = useCallback(async () => {
    setIsLoading(true);
    const result = await GET("v1/admin/drive/assets", { type });
    setIsLoading(false);
    if (result?.status) setItems(result.data || []);
  }, [type]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาเลือกไฟล์รูปภาพ");
      return;
    }
    try {
      const prepared = await prepareImage(file, { maxDim: 1200, removeWhite });
      setDraft(prepared.dataUrl);
    } catch (error) {
      await alert("", "warning", "แจ้งเตือน", error.message || "อ่านรูปภาพไม่สำเร็จ");
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      await alert("", "warning", "แจ้งเตือน", `กรุณาตั้งชื่อ${label.item}`);
      return;
    }
    if ((draft.length * 3) / 4 > MAX_BYTES) {
      await alert("", "warning", "แจ้งเตือน", "ไฟล์รูปใหญ่เกิน 5MB กรุณาใช้รูปที่เล็กลง");
      return;
    }
    setIsSaving(true);
    const result = await POST("v1/admin/drive/assets", { type, name: name.trim(), image_base64: draft });
    setIsSaving(false);
    if (result?.status) {
      setDraft(null);
      setName("");
      setMode("library");
      refresh();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "บันทึกเข้าคลังไม่สำเร็จ");
    }
  };

  const handleDelete = async (item) => {
    if (!(await alert("", "warning", "ยืนยันการลบ", `ลบ${label.item} "${item.name}" ออกจากคลัง?`, "ลบ", "ยกเลิก"))) return;
    const result = await DELETE(`v1/admin/drive/assets/${item.uid}`);
    if (result?.status) refresh();
    else await alert("", "warning", "แจ้งเตือน", result?.message || "ลบไม่สำเร็จ");
  };

  const startUpload = () => {
    setMode("upload");
    setDraft(null);
  };

  return (
    <div>
      <div>
        {draft ? (
          <div>
            <div className="fw-semibold small mb-2" style={{ color: "#22384a" }}>ตั้งชื่อแล้วบันทึกเข้าคลัง</div>
            <div className="d-flex justify-content-center p-3 mb-3" style={{ ...checker, borderRadius: 12, border: "1px solid #e2e8f0" }}>
              <img src={draft} alt="ตัวอย่าง" style={{ maxHeight: 180, maxWidth: "100%" }} />
            </div>
            <input
              type="text"
              className="form-control mb-3"
              placeholder={`ชื่อ${label.item} เช่น ลายเซ็นกรรมการ`}
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setDraft(null)} disabled={isSaving}>ย้อนกลับ</button>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                <i className="fa-solid fa-floppy-disk me-1" />บันทึกเข้าคลัง
              </button>
            </div>
          </div>
        ) : mode === "draw" ? (
          <div>
            <DrawPad
              onDone={setDraft}
              onEmpty={() => alert("", "warning", "แจ้งเตือน", "ยังไม่ได้วาดลายเซ็น")}
            />
            <button type="button" className="btn btn-link btn-sm mt-2 px-0" onClick={() => setMode("library")}>
              <i className="fa-solid fa-arrow-left me-1" />กลับไปคลัง
            </button>
          </div>
        ) : mode === "upload" ? (
          <div>
            <label className="d-flex align-items-center gap-2 small mb-3" style={{ color: "#22384a" }}>
              <input type="checkbox" checked={removeWhite} onChange={(e) => setRemoveWhite(e.target.checked)} />
              ทำพื้นหลังสีขาวให้โปร่งใส (แนะนำสำหรับรูปถ่าย/สแกนลายเซ็น)
            </label>
            <button type="button" className="btn btn-outline-primary" onClick={() => fileRef.current?.click()}>
              <i className="fa-solid fa-upload me-1" />เลือกรูปจากเครื่อง
            </button>
            <div>
              <button type="button" className="btn btn-link btn-sm mt-2 px-0" onClick={() => setMode("library")}>
                <i className="fa-solid fa-arrow-left me-1" />กลับไปคลัง
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {type === "signature" ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setMode("draw")}>
                  <i className="fa-solid fa-signature me-1" />วาดลายเซ็นใหม่
                </button>
              ) : null}
              <button type="button" className={`btn btn-sm ${type === "signature" ? "btn-outline-primary" : "btn-primary"}`} onClick={startUpload}>
                <i className="fa-solid fa-upload me-1" />อัปโหลดรูป{type === "stamp" ? " PNG" : ""}
              </button>
            </div>

            {isLoading ? (
              <div className="text-center text-secondary py-5"><i className="fa-solid fa-spinner fa-spin me-2" />กำลังโหลด...</div>
            ) : !items.length ? (
              <div className="text-center text-secondary py-5">ยังไม่มี{label.title}ในคลัง</div>
            ) : (
              <div className="row g-3">
                {items.map((item) => (
                  <div className="col-6 col-md-4" key={item.uid}>
                    <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
                      <button
                        type="button"
                        className="d-flex align-items-center justify-content-center w-100 border-0 p-2"
                        style={{ ...checker, height: 110, cursor: onPick ? "pointer" : "default" }}
                        title={onPick ? `ใช้ ${item.name}` : item.name}
                        onClick={() => onPick?.(item.image_base64, item.name)}
                      >
                        <img src={item.image_base64} alt={item.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                      </button>
                      <div className="d-flex align-items-center justify-content-between gap-2 px-2 py-1">
                        <span className="small text-truncate" style={{ color: "#22384a" }}>{item.name}</span>
                        <button type="button" className="btn btn-sm text-danger p-1" title="ลบออกจากคลัง" onClick={() => handleDelete(item)}>
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
      </div>
    </div>
  );
}
